import React, { useState } from 'react';
import { ProductPlan } from 'shared-types';

interface ProductListEditorProps {
    products: ProductPlan[];
    onChange: (newProducts: ProductPlan[]) => void;
    onBack: () => void;
}

const ProductListEditor: React.FC<ProductListEditorProps> = ({ products, onChange, onBack }) => {
    const [expandedProductId, setExpandedProductId] = useState<string | null>(null);

    const handleItemChange = (index: number, field: keyof ProductPlan, value: any) => {
        const newProducts = [...products];
        // @ts-ignore
        newProducts[index][field] = value;
        onChange(newProducts);
    };
    
    const handleFeatureChange = (productIndex: number, featureIndex: number, value: string) => {
        const newProducts = [...products];
        newProducts[productIndex].features[featureIndex] = value;
        onChange(newProducts);
    };

    const handleAddFeature = (productIndex: number) => {
        const newProducts = [...products];
        newProducts[productIndex].features.push('New Feature');
        onChange(newProducts);
    };

    const handleRemoveFeature = (productIndex: number, featureIndex: number) => {
        const newProducts = [...products];
        newProducts[productIndex].features.splice(featureIndex, 1);
        onChange(newProducts);
    };

    const handleAddItem = () => {
        const newItem: ProductPlan = {
            id: `plan_${Date.now()}`,
            name: "New Plan",
            price: "Rs. 0",
            frequency: "month",
            features: ["Feature 1"],
            isFeatured: false,
            ctaText: "Get Started",
            ctaLink: "/register"
        };
        onChange([...products, newItem]);
    };
    
    const handleRemoveItem = (index: number) => {
        const newProducts = products.filter((_, i) => i !== index);
        onChange(newProducts);
    };

    return (
        <div className="p-6">
            <div className="flex items-center gap-4 mb-6">
                <button onClick={onBack} className="text-cyan-600 dark:text-cyan-400 font-semibold hover:underline">&larr; Back to Site Overview</button>
            </div>
            <h2 className="text-xl font-bold mb-4">Editing: Product Plans</h2>
            <div className="space-y-4">
                {products.map((product, index) => (
                    <div key={product.id} className="bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="p-3 flex justify-between items-center cursor-pointer" onClick={() => setExpandedProductId(expandedProductId === product.id ? null : product.id)}>
                            <h4 className="font-bold text-slate-800 dark:text-slate-200">{product.name} ({product.price}/{product.frequency})</h4>
                            <div className="flex items-center gap-2">
                                 <button onClick={(e) => { e.stopPropagation(); handleRemoveItem(index); }} className="px-3 py-1 bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-xs font-semibold rounded-md hover:bg-red-200 dark:hover:bg-red-900/60 transition">Delete</button>
                                <span>{expandedProductId === product.id ? '🔼' : '🔽'}</span>
                            </div>
                        </div>
                        {expandedProductId === product.id && (
                             <div className="p-4 border-t border-slate-200 dark:border-slate-700 space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div><label className="text-xs font-medium text-slate-500">Name</label><input type="text" value={product.name} onChange={e => handleItemChange(index, 'name', e.target.value)} className="w-full bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md p-2 text-sm mt-1" /></div>
                                    <div><label className="text-xs font-medium text-slate-500">Price</label><input type="text" value={product.price} onChange={e => handleItemChange(index, 'price', e.target.value)} className="w-full bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md p-2 text-sm mt-1" /></div>
                                    <div><label className="text-xs font-medium text-slate-500">Frequency</label><input type="text" value={product.frequency} onChange={e => handleItemChange(index, 'frequency', e.target.value)} className="w-full bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md p-2 text-sm mt-1" /></div>
                                </div>
                                <div>
                                    <h5 className="text-sm font-semibold mb-2">Features</h5>
                                    <div className="space-y-2">
                                        {product.features.map((feature, fIndex) => (
                                            <div key={fIndex} className="flex items-center gap-2">
                                                <input type="text" value={feature} onChange={e => handleFeatureChange(index, fIndex, e.target.value)} className="flex-grow bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-md p-2 text-sm" />
                                                <button onClick={() => handleRemoveFeature(index, fIndex)} className="text-red-500 hover:text-red-700 text-xl">&times;</button>
                                            </div>
                                        ))}
                                        <button onClick={() => handleAddFeature(index)} className="text-xs font-semibold text-cyan-600 dark:text-cyan-400">+ Add Feature</button>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center pt-2">
                                    <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={product.isFeatured} onChange={e => handleItemChange(index, 'isFeatured', e.target.checked)} className="h-4 w-4 rounded text-cyan-600 focus:ring-cyan-500" /> Most Popular</label>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                <button onClick={handleAddItem} className="px-4 py-2 bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300 text-sm font-semibold rounded-full hover:bg-cyan-200 dark:hover:bg-cyan-900/60 transition">+ Add Product Plan</button>
            </div>
        </div>
    );
};

export default ProductListEditor;